import type {
  Solicitacao,
  Movimentacao,
  StatusSolicitacao,
  TipoSolicitacao,
  TipoFluxoMovimentacao,
  StatusMovimentacao,
  NivelAlertaMovimentacao,
} from '../../models';
import { calcularAlertaMovimentacao } from '../../models';

/**
 * Filtro do mini painel (SolicitacoesResumoCards). Status/tipo/projeto são da Solicitação;
 * fluxo/etapa/urgência são da Movimentação (uma Solicitação pode ter várias — ex.: Troca
 * de Projeto tem envio + devolução, ou duas devoluções). Lista vazia = sem filtro naquele
 * critério (mesma convenção de sempre).
 */
export interface SelecaoResumo {
  status: StatusSolicitacao[];
  tipos: TipoSolicitacao[];
  /** `Solicitacao.clienteId`; `SEM_PROJETO` pras que não têm. */
  clientes: string[];
  fluxos: TipoFluxoMovimentacao[];
  etapas: StatusMovimentacao[];
  niveisAlerta: NivelAlertaMovimentacao[];
}

export type FacetaResumo = keyof SelecaoResumo;

/** O que os números do painel contam — acompanha a visão da lista (ver VisaoControle). */
export type UnidadeContagem = 'movimentacoes' | 'solicitacoes';

export interface ContagensResumo {
  status: Map<StatusSolicitacao, number>;
  tipos: Map<TipoSolicitacao, number>;
  clientes: Map<string, number>;
  fluxos: Map<TipoFluxoMovimentacao, number>;
  etapas: Map<StatusMovimentacao, number>;
  niveisAlerta: Map<NivelAlertaMovimentacao, number>;
  /** Movimentações das Solicitações na tela (só as que batem com os filtros de
   * Movimentação, se houver algum). */
  movimentacoesExibidas: number;
  /** Idem, pro card "Todos" — com todos os status (ignora o filtro de status). */
  movimentacoesTodosStatus: number;
}

export const SEM_PROJETO = '__sem_projeto__';

export const FLUXO_MOVIMENTACAO_LABELS: Record<TipoFluxoMovimentacao, string> = {
  envio: 'Envio',
  devolucao: 'Devolução',
};

/** Agrupa os textos de `calcularAlertaMovimentacao` (que são muitos — "Postagem Urgente",
 * "Cobrar Entrega"...) pelo nível, que é o que interessa pra "o que tá pegando fogo". */
export const NIVEL_ALERTA_LABELS: Record<NivelAlertaMovimentacao, string> = {
  urgente: '🚨 Urgente',
  atencao: 'Atenção',
  neutro: 'Sem urgência',
  sucesso: 'Concluída',
  cancelado: 'Cancelada',
};

export function chaveCliente(solicitacao: Solicitacao): string {
  return solicitacao.clienteId || SEM_PROJETO;
}

/** A Movimentação passa nos critérios de Movimentação selecionados (menos `ignorar`)? */
export function movimentacaoAtende(
  movimentacao: Movimentacao,
  selecao: SelecaoResumo,
  ignorar?: FacetaResumo
): boolean {
  if (ignorar !== 'fluxos' && selecao.fluxos.length > 0 && !selecao.fluxos.includes(movimentacao.tipoFluxo)) {
    return false;
  }
  if (
    ignorar !== 'etapas' &&
    selecao.etapas.length > 0 &&
    !selecao.etapas.includes(movimentacao.statusMovimentacao)
  ) {
    return false;
  }
  if (
    ignorar !== 'niveisAlerta' &&
    selecao.niveisAlerta.length > 0 &&
    !selecao.niveisAlerta.includes(calcularAlertaMovimentacao(movimentacao).nivel)
  ) {
    return false;
  }
  return true;
}

function temFiltroMovimentacao(selecao: SelecaoResumo, ignorar?: FacetaResumo): boolean {
  return (['fluxos', 'etapas', 'niveisAlerta'] as const).some(
    (faceta) => faceta !== ignorar && selecao[faceta].length > 0
  );
}

/**
 * Critérios do painel aplicados à lista (menos `ignorar` — ver contarResumo). Com algum
 * critério de Movimentação ativo, a Solicitação fica se tiver PELO MENOS UMA Movimentação
 * que passe em todos eles ao mesmo tempo (ex.: "Envio" + "Em Trânsito" = tem um envio em
 * trânsito, não "tem um envio qualquer e alguma outra coisa em trânsito"). Solicitações
 * sem Movimentação (Formatação/Acessos) saem nesse caso.
 */
export function aplicarSelecaoResumo(
  lista: Solicitacao[],
  selecao: SelecaoResumo,
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  ignorar?: FacetaResumo
): Solicitacao[] {
  const filtrarMovimentacao = temFiltroMovimentacao(selecao, ignorar);
  return lista.filter((solicitacao) => {
    if (ignorar !== 'status' && selecao.status.length > 0 && !selecao.status.includes(solicitacao.status)) {
      return false;
    }
    if (ignorar !== 'tipos' && selecao.tipos.length > 0 && !selecao.tipos.includes(solicitacao.tipoSolicitacao)) {
      return false;
    }
    if (ignorar !== 'clientes' && selecao.clientes.length > 0 && !selecao.clientes.includes(chaveCliente(solicitacao))) {
      return false;
    }
    if (filtrarMovimentacao) {
      const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
      return movimentacoes.some((movimentacao) => movimentacaoAtende(movimentacao, selecao, ignorar));
    }
    return true;
  });
}

function incrementar<K>(mapa: Map<K, number>, chave: K) {
  mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
}

/**
 * Contagem de cada opção do painel — cada critério conta com todos os OUTROS filtros
 * aplicados, menos ele mesmo, pra uma opção desmarcada mostrar quantas viriam ao marcá-la
 * (e não sempre 0). Tudo na mesma unidade, a da visão da lista: em Movimentações, "Novo 14"
 * são 14 Movimentações de Solicitações novas (Formatação/Acessos, sem Movimentação, contam
 * 0); em Solicitações, "Devolução 5" são 5 Solicitações com alguma devolução.
 *
 * `base` é a lista já filtrada pelo que está fora do painel (busca por texto, mês/ano).
 */
export function contarResumo(
  base: Solicitacao[],
  selecao: SelecaoResumo,
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  unidade: UnidadeContagem
): ContagensResumo {
  const contagens: ContagensResumo = {
    status: new Map(),
    tipos: new Map(),
    clientes: new Map(),
    fluxos: new Map(),
    etapas: new Map(),
    niveisAlerta: new Map(),
    movimentacoesExibidas: 0,
    movimentacoesTodosStatus: 0,
  };
  const filtrar = (ignorar: FacetaResumo) =>
    aplicarSelecaoResumo(base, selecao, movimentacoesPorSolicitacao, ignorar);

  // Critério da Solicitação: soma 1 (Solicitações) ou as Movimentações dela que passam nos filtros.
  const peso = (solicitacao: Solicitacao) =>
    unidade === 'solicitacoes'
      ? 1
      : (movimentacoesPorSolicitacao.get(solicitacao.id) ?? []).filter((m) => movimentacaoAtende(m, selecao)).length;
  const somar = <K>(mapa: Map<K, number>, chave: K, quanto: number) => {
    if (quanto > 0) mapa.set(chave, (mapa.get(chave) ?? 0) + quanto);
  };
  for (const s of filtrar('status')) somar(contagens.status, s.status, peso(s));
  for (const s of filtrar('tipos')) somar(contagens.tipos, s.tipoSolicitacao, peso(s));
  for (const s of filtrar('clientes')) somar(contagens.clientes, chaveCliente(s), peso(s));

  // Critério da Movimentação: cada Movimentação (Movimentações) ou cada Solicitação que tem
  // alguma com aquele valor (Solicitações).
  const contarMovimentacoes = <K>(faceta: FacetaResumo, mapa: Map<K, number>, chave: (m: Movimentacao) => K) => {
    for (const solicitacao of filtrar(faceta)) {
      const chaves = (movimentacoesPorSolicitacao.get(solicitacao.id) ?? [])
        .filter((movimentacao) => movimentacaoAtende(movimentacao, selecao, faceta))
        .map(chave);
      for (const valor of unidade === 'solicitacoes' ? Array.from(new Set(chaves)) : chaves) incrementar(mapa, valor);
    }
  };
  contarMovimentacoes('fluxos', contagens.fluxos, (m) => m.tipoFluxo);
  contarMovimentacoes('etapas', contagens.etapas, (m) => m.statusMovimentacao);
  contarMovimentacoes('niveisAlerta', contagens.niveisAlerta, (m) => calcularAlertaMovimentacao(m).nivel);

  const somarMovimentacoes = (lista: Solicitacao[]) =>
    lista.reduce(
      (soma, solicitacao) =>
        soma +
        (movimentacoesPorSolicitacao.get(solicitacao.id) ?? []).filter((m) => movimentacaoAtende(m, selecao)).length,
      0
    );
  contagens.movimentacoesExibidas = somarMovimentacoes(aplicarSelecaoResumo(base, selecao, movimentacoesPorSolicitacao));
  contagens.movimentacoesTodosStatus = somarMovimentacoes(filtrar('status'));

  return contagens;
}
